<template>
  <div id="shell">
    <card v-if="!connected" :title="$t('g-shell:remoteShell')" no-footer>
      <div slot="card-body" class="shell-login">
        <!-- Fake input to prevent saved password to be filled -->
        <input id="fakeEmail" type="email" class="d-none">
        <input id="fakePassword" type="password" class="d-none">

        <div class="form-group mx-auto">
          <label for="username" class="control-label">{{ $t('g-shell:username') | capitalize }}</label>
          <input id="username"
                 v-model="username"
                 v-validate="'required'"
                 :data-vv-as="$t('g-shell:username')"
                 :class="{ 'is-invalid': errors.has('username') }"
                 type="text"
                 name="username"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('username') }}</div>
        </div>

        <div class="form-group mx-auto">
          <label for="password" class="control-label">{{ $t('g-shell:password') | capitalize }}</label>
          <input id="password"
                 v-model="password"
                 v-validate="'required'"
                 :data-vv-as="$t('g-shell:password')"
                 type="password"
                 name="password"
                 class="form-control"
                 @keyup.enter="connectToSsh"
          >
          <div class="invalid-feedback">{{ errors.first('password') }}</div>
        </div>

        <div class="text-center">
          <button id="shell-connect" :disabled="disabled || isConnecting" class="btn btn-primary" @click="connectToSsh">
            {{ isConnecting ? $t('g-shell:connectingTo', {gateway:gateway.eui}) : $t('g-shell:connectTo', {gateway:gateway.eui}) | capitalize }}
          </button>
        </div>
      </div>
    </card>

    <terminal v-if="connected" :event-bus="eventBus" @onTerminalData="onTerminalData" />
  </div>
</template>

<script>
import { isEmpty } from 'lodash-es'
import { mapGetters } from 'vuex'
import auth from '@/api/auth'
import eventBus from '@/modules/eventBus'
import constants from '@/configuration/constants'

import SocketIO from 'socket.io-client'
import EventBus from 'vue'
const Terminal = () => import(/* webpackChunkName: "l-xterm" */ './Terminal')

export default {
  titleKey: 'gatewayShell',

  components: { Terminal },

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      isConnecting: false,
      connected: false,
      sshClosed: false,

      socket: null,
      eventBus: null,

      username: '',
      password: ''
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    disabled () {
      return isEmpty(this.gateway)
    }
  },

  watch: {
    async eui () {
      // Reset data, and get data from new gateway
      this.username = ''
      this.password = ''
      await this.$nextTick()
      this.$validator.reset()
      this.connected = false
    }
  },

  created () {
    this.initWebsocket()

    // Using a empty Vue instance as event bus
    // We cannot use a store/props with a watch, it does not work when using the same key several times
    this.eventBus = new EventBus()
  },

  destroyed () {
    this.socket.disconnect()
    this.socket = null
    this.eventBus = null
  },

  methods: {
    initWebsocket () {
      // Set a boolean to not show the "Disconnected from server" messages on ssh close
      this.sshClosed = false

      // Create websocket
      const Connect = SocketIO.connect
      this.socket = new Connect(constants.remoteShellUrl + '?auth=' + auth.getLocalToken(), {
        reconnection: constants.remoteShellReconnection,
        reconnectionDelay: constants.remoteShellReconnectionDelay,
        reconnectionDelayMax: constants.remoteShellReconnectionDelayMax,
        reconnectionAttempts: constants.remoteShellReconnectionAttempts,
        timeout: constants.remoteShellTimeout
      })

      // Reinit socket listeners
      this.socket.off()

      // Unable to connect
      this.socket.on('connect_error', () => {
        eventBus.$emit('message-error', this.$t('g-shell:connectionError'))
      })

      // Unable to reconnect
      this.socket.on('reconnect_failed', () => {
        eventBus.$emit('message-error', this.$t('g-shell:reconnectionFailed'))
      })

      // Disconnected
      this.socket.on('disconnect', () => {
        // Disconnected message
        if (this.sshClosed) {
          this.eventBus.$emit('terminal-data', '\r\n[ERROR] Disconnected from server\r\n')
        }
      })

      // When receiving data from backend
      this.socket.on('data', (data) => {
        // Sometimes, the terminal receive unicode characters to move the cursor, we need to skip them
        // https://coderanch.com/t/476153/java/garbage-characters-reading-java-io
        // eslint-disable-next-line
        if (data.indexOf('7[r[999;999H[6n') !== -1) {
          return false
        }

        this.eventBus.$emit('terminal-data', data)
      })

      // When ssh connection is closed
      this.socket.on('ssh_close', () => {
        // Set a boolean to not show the "Disconnected from server" messages on ssh close
        this.sshClosed = true

        setTimeout(() => {
          // Show login form
          this.connected = false

          // Restart websocket
          // Needed to prevent websocket error when reconnecting in SSH
          this.socket.disconnect()
          this.socket = undefined
          this.initWebsocket()
        }, constants.remoteRedirectAfterDisconnection)
      })
    },

    async connectToSsh () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      // Display connecting during 2s
      this.isConnecting = true

      // Hide login form and display terminal
      setTimeout(async () => {
        this.connected = true
        this.isConnecting = false

        this.eventBus.$emit('terminal-data', '\r\nConnecting to ' + this.gateway.eui + '...\r\n')
        this.socket.emit('ssh', {
          ip: this.gateway.ip,
          eui: this.gateway.eui,
          username: this.username,
          password: this.password
        })

        // Reset form
        this.username = ''
        this.password = ''
        await this.$nextTick()
        this.$validator.reset()
      }, 2000)
    },

    // When receiving data from browser to be sent to backend
    onTerminalData (data) {
      this.socket.emit('data', data)
    }
  }
}
</script>

<style scoped lang="scss">
  .shell-login {
    .form-group {
      width: 300px;
    }
  }
</style>
